import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { OrderItemStatus } from 'src/interfaces/order-item';
import { BatchStatus } from 'src/interfaces/batch';
import { OrderMedicineService } from 'src/core/services/order-medicine.service';
import { OrderMedicine } from 'src/interfaces/order-medicine';
import { OrderMedicineItem } from 'src/interfaces/order-medicine-item';
import { AlertService } from 'src/core/services/alert.service';
import { DosageType } from 'src/interfaces/dosage-type';
import { LocalStorageService } from 'src/core/services/local-storage.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-order-medicine-list',
    templateUrl: './order-medicine-list.component.html',
    styleUrls: ['./order-medicine-list.component.scss'],
    standalone: false
})
export class OrderMedicineListComponent implements OnInit {
  batchStatuses: BatchStatus[] = [];
  orderItemStatuses: OrderItemStatus[] = [];
  orderItems: OrderMedicineItem[] = [];
  page: number = 1;
  limit: number = 10;
  totalItems: number = 0;
  savedItems: OrderMedicine[] = [];
  searchInput = new UntypedFormControl('');

  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    private _orderMedicineService: OrderMedicineService,
    private _alertService: AlertService,
    private _localStorageService: LocalStorageService,
    private _formBuilder: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    // Get saved pagination settings
    const savedLimit = this._localStorageService.get('ORDER_MEDICINE', 'LIMIT');
    if (savedLimit) {
      this.limit = parseInt(savedLimit);
    }

    // Initialize data
    this.loadOrderMedicines();
    this.loadStatuses();

    // Setup search functionality
    this.searchInput.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(value => {
        this.page = 1;
        this.loadOrderMedicines(value);
      });
  }

  onPageChange(event: PageEvent) {
    this.page = event.pageIndex + 1;
    this.limit = event.pageSize;

    this._localStorageService.store('ORDER_MEDICINE', 'LIMIT', this.limit.toString());
    this._localStorageService.store('ORDER_MEDICINE', 'PAGE', this.page.toString());
    
    this.loadOrderMedicines(this.searchInput.value);
  }

  async loadOrderMedicines(search: string = '') {
    try {
      this.savedItems = await this._orderMedicineService.getOrderMedicines(this.page, this.limit, search);
      this.totalItems = this._orderMedicineService.getTotalOrderMedicines();
      
      // Convert OrderMedicine items to OrderMedicineItems for display
      this.orderItems = [];
      if (this.savedItems && this.savedItems.length > 0) {
        for (const item of this.savedItems) {
          if (item.medicine && item.medicine_dosage_package_type) {
            const convertedItem = this.convertToOrderMedicineItem(item);
            this.orderItems.push(convertedItem);
          }
        }
      }
    } catch (error) {
      console.error('Error loading order medicines:', error);
    }
  }

  async loadStatuses() {
    // Load any necessary statuses here
  }

  convertToOrderMedicineItem(orderMedicine: OrderMedicine): OrderMedicineItem {
    if (!orderMedicine.medicine || !orderMedicine.medicine_dosage_package_type) {
      throw new Error('Missing required medicine or dosage package type');
    }
    
    // Create a default dosage type for fallback
    const defaultDosageType: DosageType = {
      id: 0,
      additional_information: 'Unknown dosage',
      default_price: 0,
      dosage_strength: 0,
      dosage_type_id: 0,
      medicine_id: orderMedicine.medicine.id,
      special_instructions: '',
      storage_directions: '',
      usage_instructions: '',
      medicine_number: '',
      is_active: 1,
      created_at: '',
      updated_at: null,
      deleted_at: null
    };
    
    // Use type assertion to avoid type checking issues
    const dosageType = (orderMedicine.medicine_dosage_package_type as any).medicine_dosage_type as DosageType || defaultDosageType;
    
    return {
      id: orderMedicine.id,
      medicine: orderMedicine.medicine,
      medicineDosagePackageType: orderMedicine.medicine_dosage_package_type,
      dosageType: dosageType,
      status: null,
      medicine_dosage_package_type_id: orderMedicine.medicine_dosage_package_type_id,
      deleted_at: undefined
    };
  }

  async removeOrderItem(item: OrderMedicineItem) {
    try {
      // If the item has an ID, delete it from the backend
      if (item.id) {
        await this._orderMedicineService.deleteOrderMedicine(item.id);
        
        // Reload all data from API to ensure UI is in sync with backend
        await this.loadOrderMedicines(this.searchInput.value);
      }
    } catch (error) {
      console.error('Error removing order medicine:', error);
      this._alertService.errorAlert('Failed to remove medicine from the order');
    }
  }
} 